import { inject, Injectable, signal } from '@angular/core';
import { toObservable } from '@angular/core/rxjs-interop';
import { Observable, catchError, distinctUntilChanged, lastValueFrom, of, switchMap, take, shareReplay } from 'rxjs';
import { environment } from '../../../environments/environment.js';
import { HttpClient } from '@angular/common/http';
import { PaymentMethodUpdateRTM } from '@gains-com/realtime';
import { LOGGER_FACTORY } from '../logger/logger-factory.provider';
import { UserViewService } from './user-view.service.js';
import { map } from 'rxjs/operators';

@Injectable({
  providedIn: 'root',
})
export class PaymentMethodsViewService {
  private readonly _http = inject(HttpClient);
  private readonly _logger = inject(LOGGER_FACTORY).create('PaymentMethodsViewService');
  private readonly _userView = inject(UserViewService);

  /**
   * Backing state. Was a per-fetch `BehaviorSubject` (recreated + completed inside
   * the pipe below); now a single persistent signal. `_stateInitialized` preserves
   * the previous `this._state?.next()` semantics — imperative mutations are no-ops
   * until the first fetch has populated state (the old optional-chain on an undefined
   * subject silently dropped them).
   */
  private readonly _state = signal<PaymentMethodUpdateRTM[] | null>(null);
  private _stateInitialized = false;
  private readonly _state$ = toObservable(this._state);
  private readonly _loading = signal<boolean>(true);

  private _fetchPaymentMethods$ = () => {
    return this._http.get<PaymentMethodUpdateRTM[]>(`${environment.apiUrl}/payment-methods`).pipe(
      take(1),
      catchError((err) => {
        this._logger.error('Failed to fetch payment methods', { error: err });
        return of(null);
      }),
    );
  };

  /**
   * Returns all payment methods connected to the user's account, excluding those that are deleted
   * but including those that are not saved (and so shouldn't be visible).
   *
   * Note: the backend will send updates for payment methods that are "deleted" so that the UI is aware that the
   * payment method is no longer usable/valid.
   *
   * Component-level logic should still explicitly exclude deleted payment methods.
   */
  paymentMethods$: Observable<PaymentMethodUpdateRTM[] | null> = this._userView.state$.pipe(
    distinctUntilChanged((u1, u2) => u1?.user_id === u2?.user_id && u1?.pii_submitted === u2?.pii_submitted),
    switchMap((user) => {
      if (!user || !user.pii_submitted) {
        this._state.set(null);
        return of(null);
      }

      return this._fetchPaymentMethods$().pipe(
        switchMap((methods) => {
          this._state.set(methods ?? []);
          this._stateInitialized = true;
          return this._state$;
        }),
      );
    }),
    shareReplay(1),
  );

  /**
   * Returns all payment methods connected to the user's account, excluding those that are deleted
   * or aren't saved.
   */
  visiblePaymentMethods$: Observable<PaymentMethodUpdateRTM[] | null> = this.paymentMethods$.pipe(
    map((pms) => pms?.filter((pm) => pm.visible_to_user) ?? null),
  );

  /** Readonly signal surface for new consumers. */
  readonly loading = this._loading.asReadonly();

  /** Backward-compat stream shim for existing observable consumers. */
  loading$: Observable<boolean> = toObservable(this._loading);

  /**
   * Replaces the entire payment methods array atomically
   * This method is optimized for full state replacements to prevent race conditions
   * where new subscriptions might get partially updated state from the cache.
   * Use this when you have the complete list of payment methods (e.g., from API response).
   *
   * @param methods Complete array of payment methods to replace the current state
   */
  setPaymentMethods(methods: PaymentMethodUpdateRTM[]) {
    if (this._stateInitialized) {
      this._state.set(methods);
    }
  }

  /**
   * Updates a single payment method from realtime events
   * This method processes a single payment method update to merge with existing state.
   * Use this for partial updates (e.g., realtime updates for specific payment methods).
   *
   * @param method The payment method to update or add
   */
  updatePaymentMethod(method: PaymentMethodUpdateRTM) {
    if (!this._stateInitialized) {
      return;
    }
    const currentMethods = this._state() ?? [];
    const updatedMethods = [...currentMethods.filter((m) => m.payment_method_id !== method.payment_method_id), method];
    this._state.set(updatedMethods);
  }

  async syncPaymentMethods() {
    this._loading.set(true);
    try {
      const methods = await lastValueFrom(this._fetchPaymentMethods$());
      if (this._stateInitialized) {
        this._state.set(methods ?? []);
      }
    } catch (err) {
      this._logger.error('Failed to sync payment methods', { error: err });
      throw err;
    } finally {
      this._loading.set(false);
    }
  }
}
